// The ⓘ next to a setting: hover, focus or tap it for a card that explains the setting with
// an animated drawing, plain words, the maths and suggested values.

import { useEffect, useId, useRef, useState } from "react";
import { HELP } from "../help/entries";
import type { HelpContext, HelpEntry } from "../help/types";

export function Help({ k, ctx }: { k: string; ctx?: HelpContext }) {
  const entry = HELP[k];
  const [open, setOpen] = useState(false);
  const [pinned, setPinned] = useState(false);
  const [above, setAbove] = useState(false);
  const timer = useRef<number | null>(null);
  const btn = useRef<HTMLSpanElement>(null);
  const id = useId();
  useEffect(() => {
    if (!open) return;
    const r = btn.current?.getBoundingClientRect();
    if (r) setAbove(r.top > window.innerHeight * 0.55);
    const esc = (e: KeyboardEvent) => { if (e.key === "Escape") { setOpen(false); setPinned(false); } };
    window.addEventListener("keydown", esc);
    return () => window.removeEventListener("keydown", esc);
  }, [open]);
  if (!entry) return null;
  const toggle = () => { setPinned(!pinned); setOpen(!pinned || !open); };
  const show = () => { if (timer.current) window.clearTimeout(timer.current); setOpen(true); };
  const hide = () => {
    if (pinned) return;
    if (timer.current) window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => setOpen(false), 180);
  };
  return (
    <span className="help" onMouseEnter={show} onMouseLeave={hide}>
      {/* a span, not a <button>: inside a <label> a button would become the label's control,
          so clicking the setting's name would open this card instead of focusing its field */}
      <span ref={btn} role="button" tabIndex={0} className="help-dot" aria-label={`About: ${entry.title}`}
        aria-expanded={open} aria-describedby={open ? id : undefined}
        onFocus={show} onBlur={hide}
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggle(); }}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); toggle(); } }}>i</span>
      {open && (
        <div id={id} role="tooltip" className={`help-card ${above ? "above" : ""}`}
          onMouseEnter={show} onMouseLeave={hide}
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}>
          <HelpCard entry={entry} ctx={ctx ?? {}} />
          {pinned && <span role="button" tabIndex={0} className="link small" onClick={() => { setPinned(false); setOpen(false); }}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setPinned(false); setOpen(false); } }}>Close</span>}
        </div>
      )}
    </span>
  );
}

export function HelpCard({ entry, ctx }: { entry: HelpEntry; ctx: HelpContext }) {
  return (
    <>
      <div className="help-title">{entry.title}</div>
      {entry.picture && <div className="help-picture">{entry.picture(ctx)}</div>}
      {entry.what.split("\n\n").map((p, i) => <p key={i}>{p}</p>)}
      {entry.math && (
        <div className="help-math">
          <div dangerouslySetInnerHTML={{ __html: entry.math }} />
          {entry.mathNote && <div className="small muted">{entry.mathNote}</div>}
        </div>
      )}
      {entry.range && <p className="small muted">{entry.range}</p>}
      {entry.scenarios && entry.scenarios.length > 0 && (
        <table className="help-scenarios">
          <thead><tr><th>Situation</th><th>Suggested</th><th>Why</th></tr></thead>
          <tbody>{entry.scenarios.map((s) => (
            <tr key={s.scenario}><td>{s.scenario}</td><td><strong>{s.value}</strong></td><td>{s.why}</td></tr>
          ))}</tbody>
        </table>
      )}
      {entry.source && <p className="small muted">{entry.source}</p>}
    </>
  );
}
